import { action } from "typesafe-actions";
import { IFiscalPeriod, IFiscalPeriodList } from "./FiscalPeriods.types";
import { IDataTableV2DateState } from "../../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { api } from "../../../api/api";
import { ERROR_MESSAGES } from "../../../constants/enums";

export const FETCH_FISCAL_PERIODS_LIST_PROGRESS =
  "FETCH_FISCAL_PERIODS_LIST_PROGRESS";
export const FETCH_FISCAL_PERIODS_LIST_SUCCESS =
  "FETCH_FISCAL_PERIODS_LIST_SUCCESS";
export const FETCH_FISCAL_PERIODS_LIST_FAILED =
  "FETCH_FISCAL_PERIODS_LIST_FAILED";

export const fetchFiscalPeriodsListProgress = () =>
  action(FETCH_FISCAL_PERIODS_LIST_PROGRESS);
export const fetchFiscalPeriodsListSuccess = (
  data: IFiscalPeriodList[],
  totalRecords: number,
) => action(FETCH_FISCAL_PERIODS_LIST_SUCCESS, { data, totalRecords });
export const fetchFiscalPeriodsListFailed = () =>
  action(FETCH_FISCAL_PERIODS_LIST_FAILED);

export const fetchFiscalPeriodsListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchFiscalPeriodsListProgress());
      let finalUrl = `/user/get-fiscal-year?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue && searchValue.length > 0) {
        finalUrl = `/user/get-fiscal-year?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&advanceFilter=${JSON.stringify(
          searchValue,
        )}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }

      const res = await api.get(finalUrl);
      const data: IFiscalPeriodList[] = res.data.data;
      dispatch(fetchFiscalPeriodsListSuccess(data, res.data.totalRecords));
    } catch (err: any) {
      dispatch(fetchFiscalPeriodsListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const FETCH_FISCAL_PERIODS_PROGRESS = "FETCH_FISCAL_PERIODS_PROGRESS";
export const FETCH_FISCAL_PERIODS_SUCCESS = "FETCH_FISCAL_PERIODS_SUCCESS";
export const FETCH_FISCAL_PERIODS_FAILED = "FETCH_FISCAL_PERIODS_FAILED";

export const fetchFiscalPeriodsProgress = () =>
  action(FETCH_FISCAL_PERIODS_PROGRESS);
export const fetchFiscalPeriodsSuccess = (data: IFiscalPeriod[]) =>
  action(FETCH_FISCAL_PERIODS_SUCCESS, { data });
export const fetchFiscalPeriodsFailed = (error: string) =>
  action(FETCH_FISCAL_PERIODS_FAILED, { error });

export const fetchFiscalPeriodsAsync =
  (year: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchFiscalPeriodsProgress());

      const res = await api.get(
        `/user/get-fiscal-year-month?fiscal_years_code=${year}`,
      );
      const data: IFiscalPeriod[] = res.data.data;

      dispatch(fetchFiscalPeriodsSuccess(data));
    } catch (err: any) {
      dispatch(fetchFiscalPeriodsFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const upsertFiscalPeriodsAsync =
  (
    data: IFiscalPeriod,
    onCallback: (isSuccess: boolean, data?: IFiscalPeriod) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post("/user/edit-fiscal-year-month", {
        ...data,
      });
      const message = "Fiscal period saved successfully.";
      dispatch(
        showMessage({
          type: "success",
          message: message,
          displayAs: "snackbar",
        }),
      );
      onCallback(true, res.data.data[0]);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const generateFiscalPeriodsReportAsync =
  (
    data: {
      fiscal_years_code: string | null;
      fiscal_year_month_code:string | null;
      period: string | null;
      year: string | null;
      from: string | null;
      to: string | null;
      report_code: string | null;
    },
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post("/accounting/generate-fiscal-closing-reports", {
        ...data,
      });
      const message = "Report created successfully.";
      dispatch(
        showMessage({
          type: "success",
          message: message,
          displayAs: "snackbar",
        }),
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const createFiscalPeriodAsync =
  (
    data: {
      year: string;
      status: string;
    },
    onCallback: (isSuccess: boolean, data?: IFiscalPeriod) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post("/user/edit-fiscal-year", {
        period: null,
        year: data.year,
        status: data.status,
      });
      const message = "Fiscal period created successfully.";
      dispatch(
        showMessage({
          type: "success",
          message: message,
          displayAs: "snackbar",
        }),
      );
      onCallback(true, res.data.data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchFiscalPeriodsYearsAsync =
  (
    onCallback: (data: string[]) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const res = await api.get(`user/get-fiscal-year-list`);
      const data: string[] = res.data.data;
      onCallback(data);
    } catch (err: any) {
      onCallback([]);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_FISCAL_PERIODS_STATE = "CLEAR_FISCAL_PERIODS_STATE";
export const clearFiscalPeriodsState = () => action(CLEAR_FISCAL_PERIODS_STATE);
